import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Download, Upload, CheckCircle, DownloadSimple } from '@phosphor-icons/react';
import { downloadExport } from '@/lib/exporter';

const monthName = (m) => new Date(2000, m - 1, 1).toLocaleString('en', { month: 'short' });

export const PharmacyInvoicesPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState([]);

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    try {
      const res = await api.get('/invoices');
      setInvoices(res.data);
    } catch (e) { console.error(e); }
  };

  const handleUploadSlip = async (invoiceId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/invoices/${invoiceId}/upload-slip`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Payment slip uploaded');
      fetchInvoices();
    } catch { toast.error('Failed to upload'); }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${invoiceNumber}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.invoices')}</h1>
        <Button data-testid="export-my-invoices-btn" variant="outline" size="sm" onClick={() => downloadExport('/exports/invoices', 'MyInvoices.xlsx')}>
          <DownloadSimple size={14} className="mr-2" /> Export
        </Button>
      </div>

      <div className="space-y-4">
        {invoices.map((inv) => (
          <Card key={inv.id} data-testid={`invoice-card-${inv.id}`} className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>{inv.invoice_number}</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {inv.period_month ? `Period: ${monthName(inv.period_month)} ${inv.period_year}` : ''} · Due: {new Date(inv.due_date).toLocaleDateString()}
                </p>
                <div className="mt-3 space-y-1 text-sm">
                  {inv.sales_total !== undefined && (
                    <>
                      <div className="flex justify-between text-slate-700"><span>Your sales this period:</span><span>RM {inv.sales_total?.toFixed(2)}</span></div>
                      <div className="flex justify-between text-green-700"><span>Less your commission:</span><span>- RM {inv.commission_earned?.toFixed(2)}</span></div>
                    </>
                  )}
                  <div className="flex justify-between"><span>Payable to HQ:</span><span>RM {inv.amount?.toFixed(2)}</span></div>
                  {inv.sst_amount > 0 && <div className="flex justify-between"><span>SST:</span><span>RM {inv.sst_amount?.toFixed(2)}</span></div>}
                  <div className="flex justify-between font-bold pt-2 border-t"><span>Total Due:</span><span>RM {inv.total_amount?.toFixed(2)}</span></div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className={`text-center px-3 py-1 rounded-full text-xs font-semibold ${
                  inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                  inv.status === 'slip_uploaded' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>{inv.status.replace('_', ' ').toUpperCase()}</span>
                <Button data-testid={`download-inv-${inv.id}`} onClick={() => downloadInvoice(inv.id, inv.invoice_number)} variant="outline" size="sm">
                  <Download size={14} className="mr-1" /> PDF
                </Button>
                {inv.status === 'unpaid' && (
                  <label className="cursor-pointer">
                    <input data-testid={`upload-slip-${inv.id}`} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && handleUploadSlip(inv.id, e.target.files[0])} />
                    <div className="inline-flex items-center px-3 py-1.5 text-white rounded-md text-sm" style={{ backgroundColor: '#1E3A8A' }}>
                      <Upload size={14} className="mr-1" /> Upload Slip
                    </div>
                  </label>
                )}
                {inv.status === 'paid' && <div className="text-green-600 text-sm flex items-center gap-1"><CheckCircle size={16} weight="fill" /> Paid</div>}
              </div>
            </div>
          </Card>
        ))}
        {invoices.length === 0 && <p className="text-center text-slate-500 py-8">{t('common.noData')}</p>}
      </div>
    </div>
  );
};
